import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';
import { getItemJSON } from '../utils/storage';
import SideMenuBar from '../components/SideMenuBar';

function Home() {
  // Department media from database
  const [departmentPhotos, setDepartmentPhotos] = useState(['/assets/images/demo.jpg']); // Fallback
  const [departmentVideos, setDepartmentVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(0);

  // Fetch active media items from backend
  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await fetch('https://tstranscareers.in/api/api/media/active');
        const data = await response.json();
        
        if (data.success && data.media) {
          // Separate photos and videos
          const photos = data.media
            .filter(item => item.type === 'photo' && item.is_active)
            .map(item => `https://tstranscareers.in/api/${item.file_path}`);
          
          const videos = data.media
            .filter(item => item.type === 'video' && item.is_active)
            .map(item => `https://tstranscareers.in/api/${item.file_path}`);
          
          // Only update if we have media items
          if (photos.length > 0) {
            setDepartmentPhotos(photos);
          }
          if (videos.length > 0) {
            setDepartmentVideos(videos);
          }
          
          console.log('✅ Media loaded:', { photos: photos.length, videos: videos.length });
        }
      } catch (error) {
        console.error('Error fetching media:', error);
        // Keep default/fallback media on error
      }
    };

    fetchMedia();
    
    // Refresh media every 30 seconds to pick up new uploads
    const interval = setInterval(fetchMedia, 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-advance video slides
  useEffect(() => {
    if (departmentVideos.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentVideo((prev) => (prev + 1) % departmentVideos.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [departmentVideos.length]);
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchLocation, setSearchLocation] = useState('');
  const [searchQualification, setSearchQualification] = useState('');
  const [videoPosters, setVideoPosters] = useState([]);
  const videoRefs = useRef([]);
  const [playingIndex, setPlayingIndex] = useState(null);
  
  const telanganaDistricts = [
    'Adilabad', 'Bhadradri Kothagudem', 'Hyderabad', 'Jagtial', 'Jangaon', 'Jayashankar Bhupalpally',
    'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 'Khammam', 'Komaram Bheem', 'Mahabubabad',
    'Mahabubnagar', 'Mancherial', 'Medak', 'Medchal–Malkajgiri', 'Mulugu', 'Nagarkurnool',
    'Nalgonda', 'Nirmal', 'Nizamabad', 'Peddapalli', 'Rajanna Sircilla', 'Ranga Reddy',
    'Sangareddy', 'Siddipet', 'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal Rural',
    'Warangal Urban', 'Yadadri Bhuvanagiri'
  ];
  const qualifications = [
    '10th Pass', '12th Pass', 'Diploma', 'ITI', 'Graduation', 'B.Tech', 'B.Sc', 'B.Com', 'BA',
    'Post Graduation', 'M.Tech', 'MBA', 'MCA', 'PhD', 'Other'
  ];
  const navigate = useNavigate();

  // Check login and role (use safe JSON helper)
  const [dashboardLink, setDashboardLink] = useState(null);
  // Attach play/pause/ended listeners to keep poster overlay in sync
  useEffect(() => {
    const cleanupFns = [];
    videoRefs.current.forEach((v, idx) => {
      if (!v) return;
      // use 'playing' so we only hide poster when frames are actually rendering
      const onPlaying = () => setPlayingIndex(idx);
      const onPauseOrEnd = () => {
        if (playingIndex === idx) setPlayingIndex(null);
      };
      v.addEventListener('playing', onPlaying);
      v.addEventListener('pause', onPauseOrEnd);
      v.addEventListener('ended', onPauseOrEnd);
      cleanupFns.push(() => {
        try {
          v.removeEventListener('playing', onPlaying);
          v.removeEventListener('pause', onPauseOrEnd);
          v.removeEventListener('ended', onPauseOrEnd);
        } catch (e) {}
      });
    });
    return () => cleanupFns.forEach(fn => fn());
  }, [departmentVideos, playingIndex]);

  const playVideo = (index) => {
    const videoEl = videoRefs.current[index];
    if (!videoEl) return;
    // Start playback; 'playing' event will update state and hide poster
    try {
      const p = videoEl.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {
      // ignore
    }
  };

  // Check login and role (use safe JSON helper)
  useEffect(() => {
    const checkLoginStatus = () => {
      const candidateLoggedIn = getItemJSON('candidateLoggedIn');
      const employerLoggedIn = getItemJSON('employerLoggedIn');
      const adminLoggedIn = getItemJSON('adminLoggedIn');

      if (candidateLoggedIn) {
        setDashboardLink({ path: '/candidate-dashboard', label: t('home.goToCandidateDashboard') });
      } else if (employerLoggedIn) {
        setDashboardLink({ path: '/employer-dashboard', label: t('home.goToEmployerDashboard') });
      } else if (adminLoggedIn) {
        setDashboardLink({ path: '/admin-dashboard', label: 'Go to Admin Dashboard' });
      } else {
        setDashboardLink(null);
      }
    };

    // Check on mount and when localStorage changes
    checkLoginStatus();

    // Listen for storage changes (including from other tabs or programmatic changes)
    const handleStorageChange = (e) => {
      if (e.key === 'employerLoggedIn' || e.key === 'candidateLoggedIn' || e.key === 'adminLoggedIn') {
        checkLoginStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically to catch same-tab localStorage changes
    const interval = setInterval(checkLoginStatus, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [t]);

  // Recent jobs state - loaded from backend
  const [recentJobs, setRecentJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);

  // Fetch recent jobs from backend
  useEffect(() => {
    const fetchRecentJobs = async () => {
      try {
        const response = await fetch('https://tstranscareers.in/api/api/jobs');
        const data = await response.json();
        
        if (data.success) {
          // Take latest 6 jobs for home page
              const jobs = data.jobs.slice(0, 6);
              setRecentJobs(jobs);
              setFilteredJobs(jobs);
          console.log('✅ Loaded recent jobs for home page:', data.jobs.length);
        }
      } catch (error) {
        console.error('Error fetching recent jobs:', error);
      }
    };

    fetchRecentJobs();
    
    // Refresh jobs every 2 minutes
    const jobsInterval = setInterval(fetchRecentJobs, 120000);
    
    return () => clearInterval(jobsInterval);
  }, []);

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % departmentPhotos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [departmentPhotos.length]);

  // Generate lightweight poster thumbnails for videos (attempt to capture first frame)
  useEffect(() => {
    let mounted = true;
    if (!departmentVideos || departmentVideos.length === 0) {
      setVideoPosters([]);
      return;
    }

    const generatePoster = (url) => {
      return new Promise((resolve) => {
        try {
          const video = document.createElement('video');
          video.crossOrigin = 'anonymous';
          video.preload = 'metadata';
          video.muted = true;
          video.playsInline = true;
          let resolved = false;

          const cleanup = () => {
            try { video.pause(); } catch (e) {}
            video.removeAttribute('src');
            video.load && video.load();
          };

          const doResolve = (val) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve(val);
          };

          // If loadeddata, seek a tiny bit then draw
          const onLoaded = () => {
            try {
              const seekTo = Math.min(0.1, video.duration || 0.1);
              const handleSeeked = () => {
                try {
                  const canvas = document.createElement('canvas');
                  canvas.width = video.videoWidth || 320;
                  canvas.height = video.videoHeight || 180;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                  const dataUrl = canvas.toDataURL('image/jpeg');
                  doResolve(dataUrl);
                } catch (err) {
                  doResolve(null);
                }
              };

              // Some browsers allow drawing immediately
              if (video.readyState >= 2) {
                handleSeeked();
              } else {
                video.addEventListener('seeked', handleSeeked, { once: true });
                try { video.currentTime = seekTo; } catch (e) { /* ignore */ }
              }
            } catch (err) {
              doResolve(null);
            }
          };

          // error or timeout fallback
          const onError = () => doResolve(null);
          const timeout = setTimeout(() => doResolve(null), 4000);

          video.addEventListener('loadeddata', onLoaded, { once: true });
          video.addEventListener('error', onError, { once: true });
          video.src = url;
        } catch (err) {
          resolve(null);
        }
      });
    };

    (async () => {
      try {
        const posters = await Promise.all(departmentVideos.map((v) => generatePoster(v)));
        if (!mounted) return;
        // fallback to demo image when poster generation failed
        setVideoPosters(posters.map(p => p || '/assets/images/demo.jpg'));
      } catch (err) {
        if (!mounted) return;
        setVideoPosters(departmentVideos.map(() => '/assets/images/demo.jpg'));
      }
    })();

    return () => { mounted = false; };
  }, [departmentVideos]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Handle job search logic here
    applyFilter(searchLocation, searchQualification);
  };

  const applyFilter = (location, qualification) => {
    // normalize
    const loc = (location || '').toString().trim().toLowerCase();
    const qual = (qualification || '').toString().trim().toLowerCase();

    if (!loc && !qual) {
      setFilteredJobs(recentJobs);
      return;
    }

    const results = recentJobs.filter((job) => {
      const jobLoc = ((job.district || job.place) || '').toString().toLowerCase();
      const jobQual = (job.qualification || '').toString().toLowerCase();

      const locMatch = !loc || (jobLoc && jobLoc.includes(loc));
      const qualMatch = !qual || (jobQual && jobQual.includes(qual));

      return locMatch && qualMatch;
    });

    setFilteredJobs(results);
  };

  // keep results live when user changes either filter (fields are optional)
  useEffect(() => {
    applyFilter(searchLocation, searchQualification);
  }, [searchLocation, searchQualification, recentJobs]);

  return (
    <div className="home">
      <SideMenuBar />
      {/* Dashboard Button for Logged-in Users */}
      {dashboardLink && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.5rem 0' }}>
          <button
            className="dashboard-btn"
            onClick={() => navigate(dashboardLink.path)}
            style={{ padding: '0.8rem 2rem', background: '#4169E1', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '1rem', cursor: 'pointer' }}
          >
            {dashboardLink.label}
          </button>
        </div>
      )}
            
       <div className="department-section">
         <div className="container">
           <div className="minister-info">
            <div className="minister-card">
              <div className="minister-photo cm">
                <img src="/assets/images/cm.png" alt="Chief Minister of Telangana" />
              </div>
              <div className="minister-details cm">
                <h3>Sri Anumula Revanth Reddy</h3>
                <p>Honorable Chief Minister, Govt of Telangana</p>
              </div>
            </div>
            <div className="minister-card">
              <div className="minister-photo state">
                <img src="/assets/images/Laxman.jpeg" alt="Minister of State" />
              </div>
              <div className="minister-details state">
                <h3>Sri Adluri Lakshman Kumar</h3>
                <p>Minister for Scheduled Castes Development Department, Tribal Welfare Department, Minorities Welfare, Department for Empowerment of Persons with Disabilities, Senior Citizens and Transgender Persons</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Department Photo & Video Slides */}
      <div className="slides-section">
        <div className="container">
          <h2>{t('home.ourDepartment')}</h2>
          <div className="slides-flex" style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            {/* Photo Slideshow */}
            <div className="slideshow" style={{ flex: 1, minWidth: 280 }}>
              <h3 style={{ textAlign: 'center', color: '#4169E1' }}>Photos</h3>
              <div className="slides-container">
                {departmentPhotos.map((photo, index) => (
                  <div
                    key={index}
                    className={`slide ${index === currentSlide ? 'active' : ''}`}
                  >
                    <img src={photo} alt={`Department ${index + 1}`} />
                  </div>
                ))}
              </div>
              <div className="slide-indicators">
                {departmentPhotos.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
            {/* Video Slideshow */}
            <div className="slideshow" style={{ flex: 1, minWidth: 280 }}>
              <h3 style={{ textAlign: 'center', color: '#4169E1' }}>Videos</h3>
              {departmentVideos.length > 0 ? (
                <>
                  <div className="slides-container">
                    {departmentVideos.map((video, index) => (
                      <div
                        key={index}
                        className={`slide ${index === currentVideo ? 'active' : ''}`}
                        style={{ display: index === currentVideo ? 'block' : 'none' }}
                      >
                        <div className="video-wrapper">
                          <video
                            ref={(el) => (videoRefs.current[index] = el)}
                            width="100%"
                            height="220"
                            controls={false}
                            playsInline
                            muted={false}
                            disablePictureInPicture
                            controlsList="nodownload noplaybackrate"
                            preload="metadata"
                            poster={videoPosters[index] || '/assets/images/demo.jpg'}
                            className={playingIndex === index ? '' : 'video-no-events'}
                          >
                            <source src={video} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                          { (videoPosters[index] || '/assets/images/demo.jpg') && (
                            <img
                              src={videoPosters[index] || '/assets/images/demo.jpg'}
                              alt={`Video preview ${index + 1}`}
                              className={`video-poster ${playingIndex === index ? 'hidden' : ''}`}
                              onClick={() => playVideo(index)}
                              onError={(e) => { try { e.currentTarget.src = '/assets/images/demo.jpg'; } catch (err) {} }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="slide-indicators">
                    {departmentVideos.map((_, index) => (
                      <button
                        key={index}
                        className={`indicator ${index === currentVideo ? 'active' : ''}`}
                        onClick={() => setCurrentVideo(index)}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  <p>No videos available at the moment.</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Videos will appear here when added by the admin.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Job Search */}
      <div className="search-section">
        <div className="container">
          <h2>{t('home.quickJobSearch')}</h2>
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-inputs">
              <div className="input-group">
                <label htmlFor="location">{t('home.location')}</label>
                <select
                  id="location"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                >
                  <option value="">Select District</option>
                  {telanganaDistricts.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label htmlFor="qualification">{t('home.qualification')}</label>
                <select
                  id="qualification"
                  value={searchQualification}
                  onChange={(e) => setSearchQualification(e.target.value)}
                >
                  <option value="">Select Qualification</option>
                  {qualifications.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" className="search-btn">{t('home.searchJobs')}</button>
          </form>
        </div>
      </div>

      {/* Recent Posted Jobs */}
      <div className="recent-jobs-section">
        <div className="container">
          <h2>{t('home.recentlyPostedJobs')}</h2>
          <div className="jobs-grid">
            {filteredJobs.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                <p>No jobs match your search criteria.</p>
              </div>
            ) : (
              filteredJobs.map((job) => {
                return (
                  <div key={job.id} className="job-card">
                    <div className="job-header">
                      <h3>{job.job_title || job.title}</h3>
                      <span className="company">{job.company_name || job.company}</span>
                    </div>
                    <div className="job-details">
                      <div className="detail-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{job.place}, {job.district}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-graduation-cap"></i>
                        <span>{job.qualification}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-money-bill-wave"></i>
                        <span>₹{job.annual_salary_from} - ₹{job.annual_salary_to}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-clock"></i>
                        <span>{new Date(job.created_at || job.postedDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button className="view-job-btn" onClick={() => window.location.href = '/login'}>{t('Apply')}</button>
                  </div>
                );
              })
            )}
          </div>
          <div className="view-all-jobs">
            <Link to="/all-jobs" className="view-all-btn">{t('home.viewAllJobs')}</Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="container">
          <h2>{t('home.portalServices')}</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-user-tie"></i>
              </div>
              <h3>{t('home.candidateServices')}</h3>
              <p>{t('home.candidateServicesDesc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-building"></i>
              </div>
              <h3>{t('home.employerServices')}</h3>
              <p>{t('home.employerServicesDesc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3>{t('home.skillDevelopment')}</h3>
              <p>{t('home.skillDevelopmentDesc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-handshake"></i>
              </div>
              <h3>{t('home.careerGuidance')}</h3>
              <p>{t('home.careerGuidanceDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;